import {useConfig} from '@aboutyou/config';
import {StreamResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/hub_page/service.types';
import {
  FONT_SIZES,
  FONT_WEIGHTS,
  HorizontalSliderProps,
  SPACING,
  SliderPagination,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useActivatePreselectedBestCouponABTest} from '../../Coupons/useActivatePreselectedBestCouponABTest';
import {HeadlineWrapper, OutfitSlider} from '../../OutfitSlider/OutfitSlider';
import {Brands} from '../Sections/Brands/Brands';
import {DiscoverActivity} from '../Sections/DiscoverActivity/DiscoverActivity';
import {DiscoverCategory} from '../Sections/DiscoverCategory/DiscoverCategory';
import {HeroBrandsSlider} from '../Sections/HeroBrandsSlider/HeroBrandsSlider';
import {ReleaseBanner} from '../Sections/ReleaseBanner/ReleaseBanner';
import {SubCategoriesSlider} from '../Sections/SubCategoriesSlider/SubCategoriesSlider';
import {CustomComponents} from '../types';

interface StreamTileListProps extends CustomComponents {
  items: StreamResponse[];
  onAddToBasketCtaClick?: ({productId}: {productId: number}) => void;
}

export const StreamTileList: React.FC<StreamTileListProps> = ({
  items,
  SliderComponent,
  ProductsComponent,
  onAddToBasketCtaClick,
}) => {
  const {device} = useConfig();
  useActivatePreselectedBestCouponABTest();

  return (
    <StreamWrapper>
      {items.map((item, index) => {
        switch (item.section.type?.$case) {
          case 'spotlight':
            return (
              <SpotlightWrapper key={`spotlight-${index}`}>
                <SliderComponent {...item.section.type.spotlight} />
              </SpotlightWrapper>
            );
          case 'products':
            return (
              <ProductsComponentWrapper key={`products-${index}`}>
                <ProductsComponent
                  streamPosition={index}
                  {...item.section.type.products}
                  onAddToBasketCtaClick={onAddToBasketCtaClick}
                  trackComponentConfig={{
                    location_id: 'products',
                  }}
                />
              </ProductsComponentWrapper>
            );
          case 'outfitSlider': {
            const outfitSliderConfig: Partial<HorizontalSliderProps> =
              device === 'desktop'
                ? {
                    slideCount: 2.7,
                    slideCountLarge: 3.7,
                    hideSideButtons: true,
                  }
                : {hideSideButtons: true};

            return (
              <StyledOutfitSlider
                key={`outfitSLider-${index}`}
                outfitSliderData={item.section.type.outfitSlider}
                streamPosition={index}
                SlideControlComponent={
                  device === 'desktop' ? SliderPagination : undefined
                }
                SlideControlComponentBottom={
                  device === 'mobile' ? SliderPagination : undefined
                }
                {...outfitSliderConfig}
              />
            );
          }
          case 'brands':
            return (
              <StyledBrands
                key={`brands-${index}`}
                {...item.section.type.brands}
                streamPosition={index}
              />
            );
          case 'heroBrands':
            return (
              <StyledHeroBrandsSlider
                {...item.section.type.heroBrands}
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                key={`heroBrands-${index}`}
                trackComponentConfig={{
                  location_id: 'heroBrands',
                }}
              />
            );
          case 'discoverCategory':
            // mobile only
            return (
              <DiscoverCategory
                {...item.section.type.discoverCategory}
                key={`discoveryCategory-${index}`}
                streamPosition={index}
              />
            );
          case 'releaseBanner':
            // mobile only
            return (
              <ReleaseBanner
                {...item.section.type.releaseBanner}
                key={`releaseBanner-${index}`}
              />
            );
          case 'discoverActivity':
            return (
              <StyledDiscoverActivity
                discoverActivity={item.section.type?.discoverActivity}
                streamPosition={index}
                key={`discoverActivity-${index}`}
              />
            );
          case 'subCategories':
            // mobile only
            return (
              <StyledSubCategoriesSlider
                elements={item.section.type?.subCategories.elements}
                key={`subCategories-${index}`}
              />
            );
          default:
            return null;
        }
      })}
    </StreamWrapper>
  );
};

const StyledDiscoverActivity = styled(DiscoverActivity)`
  padding-block: ${SPACING['Y.Xl']} ${SPACING['Y.Xl2']};

  ${tabletAndUpLinaria(`
    padding-block: 0 ${SPACING['Y.Xl6']};
  `)}
`;

const StyledSubCategoriesSlider = styled(SubCategoriesSlider)`
  padding-bottom: ${SPACING['Y.Lg']};
`;

const StyledHeroBrandsSlider = styled(HeroBrandsSlider)`
  padding-block: ${SPACING['Y.Lg']} ${SPACING['Y.Xl2']};

  ${tabletAndUpLinaria(`
    padding-block: ${SPACING['Y.Xl6']};
    margin-bottom: ${SPACING['Y.Xl']};
  `)}
`;

const ProductsComponentWrapper = styled.div`
  padding-block: ${SPACING['Y.Xl']} ${SPACING['Y.Xl2']};

  ${tabletAndUpLinaria(`
    padding-block: 0 ${SPACING['Y.Xl6']};
  `)}
`;

const SpotlightWrapper = styled.div`
  padding-bottom: ${SPACING['Y.Xl']};

  ${tabletAndUpLinaria(`
    padding-bottom: ${SPACING['Y.Xl6']};
  `)}
`;

const StyledBrands = styled(Brands)`
  padding-block: ${SPACING['Y.Lg']} ${SPACING['Y.Xl2']};

  ${tabletAndUpLinaria(`
    padding-block: 0 ${SPACING['Y.Xl6']};
  `)}
`;

const StyledOutfitSlider = styled(OutfitSlider)`
  margin-bottom: 0;
  padding-bottom: ${SPACING['Y.Xl2']};

  ${tabletAndUpLinaria(`
    padding-block: ${SPACING['Y.Xl6']};
  `)}

  header {
    ${tabletAndUpLinaria(`
      margin-top: 0;
    `)}
  }

  ${HeadlineWrapper} {
    align-items: unset;
    text-transform: unset;
    gap: 0;
    margin-bottom: -8px;

    > span {
      text-transform: uppercase;
    }

    ${tabletAndUpLinaria(`
      gap: 8px;
      font-size: ${FONT_SIZES.px32};
      font-weight: ${FONT_WEIGHTS.heavy};

      > span {
        font-size: ${FONT_SIZES.px12};
        font-weight: ${FONT_WEIGHTS.mediumHeavy};
      }
    `)}
  }
`;

const StreamWrapper = styled.section`
  ${StyledSubCategoriesSlider} + ${ProductsComponentWrapper} {
    padding-block: ${SPACING['Y.Md']} ${SPACING['Y.Xl2']};
  }

  ${ProductsComponentWrapper} {
    &:last-child {
      padding-bottom: 0;
    }
  }

  ${tabletAndUpLinaria(`
    & > * {
      padding-block: 0 ${SPACING['Y.Xl6']}
    
    & > *:last-child {
      // adding only 16px since the footer is already adding 48px
      padding-bottom: ${SPACING['Y.Lg']}
  `)}
`;
